import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { assertValidContactFields } from '@/lib/contactFields';
import type { ClinicContactKind } from '@/lib/patientRelations';

export type ClinicContactRow = Database['public']['Tables']['clinic_contacts']['Row'];

export const clinicContactsQueryKey = (clinicId: string | null, search: string, kind?: string) =>
  ['clinic-contacts', clinicId, search, kind ?? 'all'] as const;

export function useClinicContacts(
  clinicId: string | null | undefined,
  options: { search?: string; kind?: ClinicContactKind } = {},
) {
  const search = (options.search ?? '').trim();
  const kind = options.kind;
  return useQuery({
    queryKey: clinicContactsQueryKey(clinicId ?? null, search, kind),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicContactRow[]> => {
      if (!clinicId) return [];
      let query = supabase
        .from('clinic_contacts')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('name', { ascending: true })
        .limit(100);

      if (kind) {
        query = query.eq('kind', kind);
      }
      if (search) {
        const pattern = `%${search}%`;
        query = query.or(
          `name.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern},cvr.ilike.${pattern}`,
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export interface ClinicContactInput {
  name: string;
  kind: ClinicContactKind;
  email?: string | null;
  phone?: string | null;
  cvr?: string | null;
  notes?: string | null;
  address?: {
    street?: string;
    zip_code?: string;
    city?: string;
    country?: string;
  };
}

export function useCreateClinicContact(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClinicContactInput): Promise<ClinicContactRow> => {
      if (!clinicId) throw new Error('No active clinic');
      assertValidContactFields(input.email, input.phone);
      const { data, error } = await supabase
        .from('clinic_contacts')
        .insert({
          clinic_id: clinicId,
          name: input.name.trim(),
          kind: input.kind,
          email: input.email?.trim() || null,
          phone: input.phone?.trim() || null,
          cvr: input.cvr?.trim() || null,
          notes: input.notes?.trim() || null,
          address: input.address ?? {},
        })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-contacts', clinicId] });
    },
  });
}

export function useUpdateClinicContact(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClinicContactInput & { id: string }): Promise<ClinicContactRow> => {
      assertValidContactFields(input.email, input.phone);
      const { data, error } = await supabase
        .from('clinic_contacts')
        .update({
          name: input.name.trim(),
          kind: input.kind,
          email: input.email?.trim() || null,
          phone: input.phone?.trim() || null,
          cvr: input.cvr?.trim() || null,
          notes: input.notes?.trim() || null,
          address: input.address ?? {},
        })
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-contacts', clinicId] });
    },
  });
}

export function useDeleteClinicContact(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('clinic_contacts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-contacts', clinicId] });
    },
  });
}
